import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useContext, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { eColors } from "../../global_vars";
import CardsContext from "../../services/cards/cards";
import UserContext from "../../services/user/user";
import ECard from "../_elements/ecard";
import EInfoToolTip from "../_elements/eInfo-tooltip";

const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

const getCardStatus = (percentage, currentValue) => {
  if (percentage == null && currentValue == null) return "noData";
  if (percentage == null) return "success";
  if (percentage > 0) return "success";
  if (percentage === 0) return "warning";
  return "error";
};

const formatPercentage = (percentage, formatNumber) => {
  if (percentage == null) return "-";

  const formattedValue = formatNumber(percentage) + "%";
  return percentage > 0 ? `+${formattedValue}` : formattedValue;
};

export default function GenericCard({
  titleId,
  dataPath,
  showCurrency = false,
  tooltipAbsoluteId,
  tooltipPercentId,
  containerClass = "widget-col-3",
  neutralSurface = false,
  "data-testid": dataTestId
}) {
  const { currency, formatNumber } = useContext(UserContext);
  const { cardsData } = useContext(CardsContext);

  const card = useMemo(() => {
    if (!cardsData) return null;
    return getNestedValue(cardsData, dataPath);
  }, [cardsData, dataPath]);

  const currentValue = card?.current_value;
  const percentage = card?.percentage;
  const status = useMemo(
    () => getCardStatus(percentage, currentValue),
    [percentage, currentValue]
  );

  if (!card) {
    return (
      <div className={containerClass} data-testid={dataTestId}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  const displayValue =
    currentValue != null ? (
      `${formatNumber(currentValue, 0)}${
        showCurrency ? currency?.symbol ?? "" : ""
      }`
    ) : (
      <FormattedMessage id="noData" />
    );

  return (
    <div className={containerClass}>
      <ECard
        title={<FormattedMessage id={titleId} />}
        value={displayValue}
        percent={formatPercentage(percentage, formatNumber)}
        status={status}
        neutralSurface={neutralSurface}
        action={
          <EInfoToolTip
            status={percentage == null ? "noData" : undefined}
            msg={
              <ul className="tooltip-list">
                <li>
                  <strong style={{ color: eColors[0] }}>
                    <FormattedMessage id="absoluteNumber" />:
                  </strong>{" "}
                  <FormattedMessage id={tooltipAbsoluteId} />
                </li>
                <li>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: `${status}.main`
                    }}
                  >
                    <FormattedMessage id="percentFigure" />:
                  </Typography>{" "}
                  <FormattedMessage id={tooltipPercentId} />
                </li>
              </ul>
            }
            data-testid={dataTestId ? `${dataTestId}_tooltip` : undefined}
            tooltipContentTestId={
              dataTestId ? `${dataTestId}_tooltip_content` : undefined
            }
          />
        }
        data-testid={dataTestId ? `${dataTestId}_box` : undefined}
        cardHeaderTestId={dataTestId ? `${dataTestId}_heading` : undefined}
        cardValueTestId={dataTestId ? `${dataTestId}_data1` : undefined}
        cardPercentTestId={dataTestId ? `${dataTestId}_data2` : undefined}
        cardChartTestId={dataTestId ? `${dataTestId}_chart` : undefined}
      />
    </div>
  );
}
